RivalIQ.module('settingsPersistence', function (settingsPersistence, App, Backbone, Marionette, $, _) {

    'use strict';

    /**
     * The persistence types.
     **/
    const types = {
        ACCOUNT: 1,
        LANDSCAPE: 2,
        USER: 3,
        VIEW: 4,
        SESSION: 5
    };

    (function () {

        const Landscape = Backbone.Model.extend({
            urlRoot: '/api/landscape'
        });

        const UpdateAccountSettings = Backbone.Model.extend({
            urlRoot: '/api/account'
        });

        const User = Backbone.Model.extend({
            urlRoot: '/api/users'
        });

        const View = Backbone.Model.extend({
            urlRoot: '/api/users',
            save: function (data) {
                const me = this;

                const state = data.settings;
                data = _.extend(_.omit(data, 'settings'), {
                    state: state
                });
                return Backbone.Model.prototype.save.call(me, data);
            }
        });

        const Session = Backbone.Model.extend({
            urlRoot: '/api/session',
            save: function (data) {
                const me = this;
                const state = data.settings;
                data = _.extend({}, state, _.omit(data, 'id', 'settings'));
                return Backbone.Model.prototype.save.call(me, data);
            }
        });

        /**
         * Persistence strategies specified by type.
         **/
        const strategies = settingsPersistence.strategies = {};

        /**
         * Persistence strategy for session state.
         **/
        strategies[types.SESSION] = {
            createModel: function () { return new Session(); },
            getId: function () { return App.state.user.get('id'); },
            getSettings: function () { return App.settings.sessionSettings || {}; },
            updateSettings: function (settings) { App.settings.sessionSettings = settings; }
        };

        /**
         * Persistence strategy for view state.
         **/
        strategies[types.VIEW] = {
            createModel: function () { return new View(); },
            getId: function () { return App.state.user.get('id'); },
            getSettings: function () { return App.settings.userState || {}; },
            updateSettings: function (settings) { App.settings.userState = settings; },
            postUpdateActions: function (state, model) {
                const clientUserSettings = state.get('userSessionSettings');
                const serverUserSettings = model.get('userSessionSettings');

                if (!_.isEqual(clientUserSettings, serverUserSettings)) {
                    state.set('userSessionSettings', serverUserSettings, {
                        silent: true
                    });

                    state.trigger('reset:userSessionSettings');
                }
            }
        };

        /**
         * Persistence strategy for user state.
         **/
        strategies[types.USER] = {
            createModel: function () { return new User(); },
            getId: function () { return App.state.user.get('id'); },
            getSettings: function () { return App.settings.userSettings || {}; },
            updateSettings: function (settings) {
                App.settings.userSettings = settings;
                App.trigger('reset:userSettings');
            }
        };

        /**
         * Persistence strategy for account settings.
         **/
        strategies[types.ACCOUNT] = {
            createModel: function () { return new UpdateAccountSettings(); },
            getId: function () { return App.state.account.get('id'); },
            getSettings: function () { return App.settings.accountSettings || {}; },
            updateSettings: function (settings) { App.settings.accountSettings = settings; }
        };

        /**
         * Persistence strategy for landscape settings.
         **/
        strategies[types.LANDSCAPE] = {
            createModel: function () { return new Landscape(); },
            getId: function () { return App.state.view.get('landscapeId'); },
            getSettings: function () { return App.settings.activeLandscapeSettings || {}; },
            updateSettings: function (settings) { App.settings.activeLandscapeSettings = settings; },
            load: function (id) {
                return new Landscape({
                    id: id
                }).fetch();
            }
        };

        /**
         * Saves settings given a strategy.
         * @param strategy
         * @param action
         **/
        const saveByStrategy = function (state, strategy, action) {

            let actionResult;

            // get the id for these settings
            const id = strategy.getId();
            // it is possible that settings won't exist
            const settings = strategy.getSettings() || {};
            // create a new model for the settings class
            const model = strategy.createModel();

            // set up the data to save
            const data = {
                id: id
            };

            if (typeof action === 'function') {

                actionResult = action(settings);

                if (actionResult) {

                    _.extend(data, actionResult);
                }
            }

            // update the strategy object on the page
            strategy.updateSettings(settings);

            // extend the data with settings no matter what
            _.extend(data, {
                settings: settings
            });

            // persist changes to the server, and return promise
            const deferred = model.save(data);

            deferred.fail(function () {

                App.execute('alert:danger', 'We had a problem saving your settings to the server. Please try again.');
            });

            if (state) {
                const changedAttributes = state.changedAttributes();

                deferred.done(function () {
                    state.trigger('persisted');

                    _.each(changedAttributes, function (v, k) {
                        state.trigger(`persisted:${k}`, v);
                    });

                    if (strategy.postUpdateActions) {
                        strategy.postUpdateActions(state, model);
                    }
                });
            }

            return deferred;
        };

        /**
         * Persists settings of a specified type with an optional action to take before saving.
         * @param type
         * @param action
         **/
        const save = settingsPersistence.save = function (type, state, action) {

            // NOTE we do not use the convenience alias (strategies) primarily
            // for the purpose of testing
            const strategy = settingsPersistence.strategies[type];

            if (!strategy) {

                return false;
            }

            return saveByStrategy(state, strategy, action);
        };

        const load = settingsPersistence.load = function (type, callback) {

            const strategy = settingsPersistence.strategies[type];
            const deferred = RivalIQ.utils.async.callbackToDeferred(callback);

            if (!strategy) {

                return deferred.reject();
            }

            strategy.load(strategy.getId())
                .done(deferred.resolve)
                .fail(deferred.reject);
        };

        settingsPersistence.account = {
            save: _.bind(save, null, types.ACCOUNT)
        };

        settingsPersistence.landscape = {
            load: _.bind(load, null, types.LANDSCAPE),
            save: _.bind(save, null, types.LANDSCAPE)
        };

        settingsPersistence.user = {
            save: _.bind(save, null, types.USER)
        };

        settingsPersistence.view = {
            save: _.bind(save, null, types.VIEW)
        };

        settingsPersistence.session = {
            save: _.bind(save, null, types.SESSION)
        };

    }());

    App.commands.setHandler('persistence:account:save', function (fn, state) {

        settingsPersistence.account.save(state, fn);
    });

    App.commands.setHandler('persistence:landscape:save', function (fn, state) {

        settingsPersistence.landscape.save(state, fn);
    });

    App.commands.setHandler('landscape:get', function (callback) {

        settingsPersistence.landscape.load(callback);
    });

    App.commands.setHandler('persistence:user:save', function (fn, state) {
        settingsPersistence.user.save(state, fn);
    });

    App.commands.setHandler('persistence:view:save', function (fn, state) {
        settingsPersistence.view.save(state, fn);
    });

    App.commands.setHandler('persistence:session:save', function (fn, state) {
        settingsPersistence.session.save(state, fn);
    });

});
